import { z } from 'zod';
import * as FlatModels from '../../internal';

type MinimalNode = Pick<FlatModels.NodeEntityModels.NodeEntityType, 'id' | 'parentId' | 'restricted' | 'route'>;
type NodeAccessInfo<T> = {
  node: T,
  restricted: boolean,
  restrictedInherited: boolean,
  unlocked: boolean,
  canAccess: boolean,
  canPassthrough: boolean,
  index: number
}

export const Role = z.enum([
  'admin',
  'editor',
  'author',
  'viewer',
  'robot'
]);
export type Role = z.infer<typeof Role>;

export const RoleArray = Object.values(Role.enum).map(value => { return { value: value, name: 'enum.user.' + value }; });


export const UserSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().min(1).max(255),
  lastName: z.string().min(1).max(255),
  email: z.string().email(),
  apiKey: z.string().nullable().optional(),
  lastLogin: z.coerce.date().nullable().default(null),
  password_confirm: z.string().max(255).optional(),
  resetPasswordToken: z.string().max(255).nullable().default(null),
  resetPasswordExpires: z.coerce.date().nullable().default(null),
  role: Role.default(Role.enum.viewer),
})

export type User = z.infer<typeof UserSchema>;

export const UserEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: UserSchema,
});

export type UserEntityQueryResult = z.infer<typeof UserEntityQueryResultSchema>;


export const AuthUserSchema = z.object({
  openId: z.string().uuid(),
  id: z.string().uuid().optional(),
  email: z.string(), // TODO: change to type emai when anonymous users are migrated to keycloak
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  sessionIp: z.string(),
  userAgent: z.string(),
  readGeneral: z.boolean().optional(),
  readItems: z.record(z.string(), z.boolean()).optional(),
  writeItems: z.record(z.string(), z.boolean()).optional(),
  readParentItems: z.record(z.string(), z.boolean()).optional(),
  role: Role.default(Role.enum.viewer),
  locale: z.string().optional(),
});


export type AuthUser = z.infer<typeof AuthUserSchema>;

export const hasNodeAccess = (user: AuthUser | undefined, node: FlatModels.NodeEntityModels.Node) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.readGeneral) return true;
  if (user.writeItems?.[node.id]) return true;
  if (user.readItems?.[node.id]) return true;
  return false;
}



const authorAccessRoles: Set<FlatModels.UserEntityModels.Role> = new Set(['admin', 'author', 'editor']);

export const canAccessAuthor = (user: AuthUser | undefined | null) => {
  if (!user) return false;
  return authorAccessRoles.has(user.role);
}


export const generalReadRoles: Set<FlatModels.UserEntityModels.Role> = new Set(['admin', 'author', 'editor']);

export const canReadNode = (user: AuthUser | undefined | null, node: MinimalNode, parentMode = false) => {
  if (!user) return false;
  if (generalReadRoles.has(user.role)) return true;
  if (user.readGeneral) return true;
  if (parentMode) {
    if (user.readParentItems?.[node?.id]) return true;
  } else {
    if (user.readItems?.[node?.id]) return true;
  }
  return false;
}

export const canWriteNode = (user: AuthUser | undefined | null, node: MinimalNode) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.writeItems?.[node?.id]) return true;
  return false;
}

export function sortByTreeDepth<T extends MinimalNode>(items: T[]) {
  const depthMap = new Map<string, number>();
  const itemsMap = new Map(items.map((item) => ([item.id, item])))

  function getDepth(id: string, ttl = 50): number {
    if (ttl < 0) throw new Error('Depth Map Timeout, maybe there is a circular reference in the tree');
    const d = depthMap.get(id);
    if (d) return d;

    const item = itemsMap.get(id)
    if (!item || item.parentId === null) {
      depthMap.set(id, 0);
      return 0;
    }
    const depth = 1 + getDepth(item.parentId, ttl - 1);
    depthMap.set(id, depth);
    return depth;
  }

  items.forEach(item => getDepth(item.id));

  return items.toSorted((a, b) => (depthMap.get(a.id) ?? 0) - (depthMap.get(b.id) ?? 0));
}

/**
 * Builds a map of access info for a given set of nodes
 * @param _nodes requires a complete hierarchy of nodes (no circular references)
 * @param user 
 */
export const getAccessInfoMap = <T extends MinimalNode>(_nodes: T[], user: AuthUser | undefined | null) => {

  const accessInfoMap = new Map<string, NodeAccessInfo<T>>();
  const sortedNodes = sortByTreeDepth(_nodes);

  sortedNodes.forEach((node, index) => {
    if (accessInfoMap.has(node.id)) return;
    const parentInfo = node.parentId ? accessInfoMap.get(node.parentId) : undefined;
    // throw if parentId is given but parent not found (tree was not complete then)
    if (node.parentId && !parentInfo) throw new Error('Access Info Map Error, parent not found');

    const restricted = node.restricted;
    const restrictedInherited = node.restricted || parentInfo?.restrictedInherited || false;
    const unlocked = parentInfo?.unlocked || canReadNode(user, node);
    const canPassthrough = canReadNode(user, node, true);
    const canAccess = restrictedInherited ? (unlocked || canPassthrough) : true; 

    accessInfoMap.set(node.id, {
      node, restricted, restrictedInherited, unlocked, canPassthrough, canAccess, index
    })
  })

  return accessInfoMap;
}

/**
 * Applies read ACL to a node
 * @param _nodes requires a complete set of node and parent nodes
 * @param user 
 */
export const applyReadACL = <T extends MinimalNode>(_nodes: T[], user: AuthUser | undefined | null, allowLastRestricted = true) => {

  if (_nodes.length === 0) return { ok: true, nodes: [] };

  try {
    const accessInfoMap = getAccessInfoMap(_nodes, user);
    const accessInfos = Array.from(accessInfoMap.values()).sort((a, b) => a.index - b.index);
    const nodes = accessInfos.map(info => info.node);

    const lastInfo = accessInfos[accessInfos.length - 1];
    if (lastInfo.canAccess) return { ok: true, nodes }

    const canAccessParent = accessInfos[accessInfos.length - 2]?.canAccess ?? true;
    if (allowLastRestricted && lastInfo?.restricted && canAccessParent) return { ok: true, nodes }

    const allowedNodes: T[] = [];
    for (const info of accessInfos) {
      if (!info.canAccess) {
        if (info.restricted) {
          allowedNodes.push(info.node);
        }
        break;
      };
      allowedNodes.push(info.node);
    }

    const redirectTo = '/' + allowedNodes.map(node => node.route).join('/');
    return { ok: false, nodes: allowedNodes, redirectTo }

  } catch {
    return { ok: false, nodes: [], redirectTo: '/' };
  }
}